/**
 * 注册邀请码工具
 * - 从 URL 读取 inviteCode / invite_code 参数
 * - sessionStorage 持久化（会话级别）
 * - 提交时自动 trim & 截断至 64 字符
 *
 * 本文件只负责 URL 解析和 sessionStorage 读写，不包含 API 调用。
 */

/** sessionStorage 中存储邀请码的 key（保留旧 key，避免影响已有链接流程） */
const INVITE_CODE_KEY = "channel_invite_code";

export function normalizeInviteCode(code: string): string {
  return code.trim().slice(0, 64);
}

/**
 * 从当前 URL 的 query string 中读取邀请码
 * 兼容 inviteCode 和 invite_code 两种参数名
 * 读取后自动 trim，不合法则返回空字符串
 */
export function readInviteCodeFromURL(): string {
  const params = new URLSearchParams(window.location.search);
  const raw = params.get("inviteCode") || params.get("invite_code") || "";
  return normalizeInviteCode(raw);
}

/**
 * 将邀请码写入 sessionStorage（仅在非空时写入）
 */
export function saveInviteCode(code: string): void {
  const trimmed = normalizeInviteCode(code);
  if (trimmed) {
    sessionStorage.setItem(INVITE_CODE_KEY, trimmed);
  }
}

/**
 * 从 sessionStorage 读取邀请码
 */
export function getInviteCode(): string {
  return sessionStorage.getItem(INVITE_CODE_KEY) || "";
}

/**
 * 清除 sessionStorage 中的邀请码
 * 在注册/登录成功后调用
 */
export function clearInviteCode(): void {
  sessionStorage.removeItem(INVITE_CODE_KEY);
}

/**
 * 从 URL 读取邀请码并自动存入 sessionStorage
 * 应在应用初始化或进入注册/登录页时调用
 * 如果 URL 中有邀请码，会覆盖 sessionStorage 中已有的
 * 如果 URL 中没有邀请码，不会覆盖已有的
 * @returns 最终生效的邀请码（可能来自 URL 或 sessionStorage）
 */
export function captureInviteCode(): string {
  const url = new URL(window.location.href);
  const captured = captureInviteCodeFromURL(url);
  if (captured) {
    // 清除 URL 中的邀请码参数，避免刷新时重复处理
    window.history.replaceState({}, "", url.toString());
    return getInviteCode();
  }
  return getInviteCode();
}

/**
 * 从指定 URL 对象中采集邀请码并删除相关参数。
 * 调用方负责决定是否 replaceState。
 */
export function captureInviteCodeFromURL(url: URL): boolean {
  const raw = url.searchParams.get("inviteCode") || url.searchParams.get("invite_code") || "";
  const code = normalizeInviteCode(raw);

  if (!code) {
    return false;
  }

  saveInviteCode(code);
  url.searchParams.delete("inviteCode");
  url.searchParams.delete("invite_code");
  return true;
}
